import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ArrowUpRight, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { CONTACT, PROJECT_TYPES } from "@/lib/content";
import { Reveal } from "@/components/Reveal";

const EMPTY = { name: "", phone: "", email: "", project_type: PROJECT_TYPES[0], details: "" };

export default function QuoteForm() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Request received — we'll reply within one business day.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Try calling us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-section" className="py-[120px]"
      style={{ background: "linear-gradient(135deg, var(--ink-2), var(--ink) 72%)", borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
      <div className="wrap grid gap-14 lg:grid-cols-2 lg:items-center">
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="inline-block w-9 h-px" style={{ background: "var(--brass-bright)" }} />
            <span className="font-mono-lbl text-[11px]" style={{ color: "var(--brass-bright)" }}>Start Your Project</span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05]" style={{ color: "var(--champagne)" }}>
            Get a free,<br />no-pressure quote.
          </h2>
          <p className="mt-5 max-w-[440px] text-[16px] leading-relaxed" style={{ color: "var(--stone)" }}>
            Tell us about your space and we'll follow up within one business day with next steps,
            timeline, and a firm estimate — no site visit required to get started.
          </p>

          <div className="mt-9 flex flex-col gap-4">
            <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-center gap-3 text-[15px] transition-colors hover:text-[var(--brass-bright)]" style={{ color: "var(--champagne)" }}>
              <Phone size={16} style={{ color: "var(--brass)" }} /> {CONTACT.phone}
            </a>
            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-3 text-[15px] transition-colors hover:text-[var(--brass-bright)] break-all" style={{ color: "var(--champagne)" }}>
              <Mail size={16} style={{ color: "var(--brass)" }} /> {CONTACT.email}
            </a>
            <div className="flex items-center gap-3 text-[15px]" style={{ color: "var(--stone)" }}>
              <MapPin size={16} style={{ color: "var(--brass)" }} /> {CONTACT.region}
            </div>
          </div>

          <a
            href={CONTACT.visualizer}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="visualizer-cta-form"
            className="btn btn-ghost mt-8"
          >
            Try the Floor Visualizer <ArrowUpRight size={16} />
          </a>
        </Reveal>

        <Reveal delay={0.1}>
          <div data-testid="quote-form-card" className="p-9" style={{ border: "1px solid var(--line)", background: "rgba(239,230,211,0.02)" }}>
            {done ? (
              <div data-testid="quote-success" className="flex flex-col items-start gap-5 py-8">
                <span className="flex items-center justify-center rounded-full" style={{ width: 54, height: 54, background: "rgba(201,162,75,0.12)", border: "1px solid var(--brass)" }}>
                  <Check size={26} style={{ color: "var(--brass-bright)" }} />
                </span>
                <h3 className="font-serif text-2xl" style={{ color: "var(--champagne)" }}>Request received.</h3>
                <p className="text-[15px]" style={{ color: "var(--stone)" }}>
                  Thank you — a LuxeBond specialist will reach out within one business day. Keep an eye on your phone.
                </p>
                <button onClick={() => setDone(false)} className="btn btn-ghost mt-1" data-testid="quote-reset">
                  Submit another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Name">
                    <input data-testid="quote-name" className="field-input" value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                  </Field>
                  <Field label="Phone">
                    <input data-testid="quote-phone" className="field-input" value={form.phone} onChange={set("phone")} placeholder="(918) 555-0142" />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Email (optional)">
                    <input data-testid="quote-email" type="email" className="field-input" value={form.email} onChange={set("email")} placeholder="jane@email.com" />
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Project Type">
                    <select data-testid="quote-type" className="field-input" value={form.project_type} onChange={set("project_type")}>
                      {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="mt-4">
                  <Field label="Project Details">
                    <textarea data-testid="quote-details" className="field-input resize-y min-h-[92px]" value={form.details} onChange={set("details")} placeholder="Approx. square footage, current floor, timeline…" />
                  </Field>
                </div>
                <button type="submit" disabled={loading} data-testid="quote-submit" className="btn btn-primary w-full justify-center mt-2 disabled:opacity-60">
                  {loading ? "Sending…" : "Request Your Consultation"}
                </button>
                <p className="text-center text-[11.5px] mt-4" style={{ color: "var(--stone-dim)" }}>
                  No obligation. No spam. Just a floor plan.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono-lbl text-[10px] mb-2" style={{ color: "var(--stone)" }}>{label}</span>
      {children}
    </label>
  );
}
